import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { isOnline } from "../utils/network";
import { getPendingOrderCount, syncPendingOrders } from "../services/offlineOrderService";

export const ConnectivityContext = createContext(null);

export const ConnectivityProvider = ({ children }) => {
  const [networkOnline, setNetworkOnline] = useState(isOnline());
  const [mode, setMode] = useState(() => localStorage.getItem("app_mode") || "auto");
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const effectiveMode = useMemo(() => {
    if (mode === "auto") {
      return networkOnline ? "online" : "offline";
    }
    return mode;
  }, [mode, networkOnline]);

  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingOrderCount();
    setPendingSyncCount(count);
    return count;
  }, []);

  const runSync = useCallback(async () => {
    if (!isOnline()) {
      return { total: 0, synced: 0, failed: 0 };
    }
    setSyncing(true);
    try {
      const result = await syncPendingOrders();
      await refreshPendingCount();
      return result;
    } finally {
      setSyncing(false);
    }
  }, [refreshPendingCount]);

  useEffect(() => {
    localStorage.setItem("app_mode", mode);
  }, [mode]);

  useEffect(() => {
    const onOnline = async () => {
      setNetworkOnline(true);
      await runSync();
    };
    const onOffline = () => {
      setNetworkOnline(false);
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    refreshPendingCount();
    if (isOnline()) {
      runSync();
    }

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshPendingCount, runSync]);

  const value = {
    mode,
    setMode,
    networkOnline,
    isOnline,
    effectiveMode,
    pendingSyncCount,
    refreshPendingCount,
    runSync,
    syncing,
  };

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
};

